import { FC, useMemo } from "react"
import { useQueryClient } from "react-query"
import { Formik, Form, FormikValues } from "formik"
import * as Yup from 'yup'
import { 
  FormikFileUpload,
  StatusErrorMessage,
  ApplicationFileDownload,
  FormikNotesArea
} from "../../../../../components/widgets"

import { doFormSubmit, processNoteValues } from "../../../../../components/helpers"

import { pick, pickBy} from "lodash"
import { DLMROI, submitDLMPhase_3_1_4_3 } from "../../../../core/doclabel"

import { SubmitButtonRow } from "../../../../../components/layout"

const DLM_ROI_SELECTED_FIELDS = [
  'roiId',
  'roiAttachmentId',
] 
  
const initSubmitSignedROIValues = {
  roiModels: [],
  note: '',
  noteAttachments: [],    
}

const schema = Yup.object({
  roiModels: Yup.array().of(
    Yup.object().shape({
      roiId: Yup.number().integer().label('ROI Id'),
      roiAttachmentId: Yup.number().integer().moreThan(0,'ROI required').required().label('ROI Attachment'),
      roiAttachmentIdAttachmentName: Yup.string().trim(),
    })
  ).min(1, 'ROI Required').required(),    
  note: Yup.string().trim().label('Note'),  
  noteAttachments: Yup.array(),    
})

type Props = {
  id: number
  appID: number
  rois: Array<DLMROI>
  doClose: Function
}

const SubmitSignedROIForm: FC<Props> = ({id, appID, rois, doClose}) => {
  const queryClient = useQueryClient();

  const initValues = useMemo(() => {    
    
    let _inits = {...initSubmitSignedROIValues};
    
    if (! (rois && rois.length > 0)) return _inits;

    Object.assign(_inits, { 
      roiModels: (rois ?? []).map( roi => ({
        ...pick(roi, ['cbRef','no','split','series','projectName', 'draftROIFile']),
        roiId: roi?.id ?? 0,
        roiAttachmentId: 0,
        roiAttachmentIdAttachmentName: ''
      }))
    });
        
    return _inits;

  }, [rois]);
  
  const doSubmit = async (values: any, actions: FormikValues) => {        

    const { setStatus, setSubmitting } = actions;
    const _models = values.roiModels.map( (model: any) => pickBy(model, (value, key) => (value && (DLM_ROI_SELECTED_FIELDS.includes(key)))));

    const payload = { 
      ...pickBy(values, (value, key) => (value && (! ['roiModels','note','noteAttachments'].includes(key)))),
      siteApplicationid: id, 
      roiModels: _models
    };

    processNoteValues(values, payload);
    
    await doFormSubmit(submitDLMPhase_3_1_4_3, payload, setStatus, setSubmitting, () => {
      queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appID } ]);
      doClose();                            
    }, true);
  }

  return(
  <>
  <Formik
    initialValues={initValues}
    validationSchema={schema}
    enableReinitialize={true}
    onSubmit={doSubmit}
  >
  {({status, values, validateField}) => {
    return (
    <>
    <Form id='kt_doclabel_submit_signedroi_form' className='form' noValidate>
      <div className='d-flex flex-column px-2' >
        <div className="fw-bold fs-6 mb-2">Site ROI:</div>
        <div className='text-muted fs-7'>Please dowload and endorse before submitting.</div>                           
        <div className='separator border-gray-200 mb-2'></div>
        <div className="mh-250px overflow-auto pe-4">        {
          values.roiModels.map((roiModel: any, idx: number) => (
          <div key={`setROIrow_${idx}`} className="row mb-4">
            <FormikFileUpload 
              size="8" 
              label={`[${roiModel?.cbRef ? roiModel.cbRef : `${roiModel.series}${roiModel.no}${roiModel?.split ? roiModel?.split :''}`}] ${roiModel.projectName}`}
              name={`roiModels.${idx}.roiAttachmentId`} 
              acceptFiles="ApplicantFileFormats" 
              required={true}   
              subTitle="Upload signed ROI here."   
              onUpdateFunc={ () => {
                validateField('roiModels');
              }}                   
            />

            <div className="col-4">        
              { roiModel.draftROIFile && 
              <>
              <div className="text-muted fs-7">Draft ROI</div>
              <ApplicationFileDownload file={roiModel.draftROIFile} />
              </>
              } 
            </div>              
          </div>
          ))        
          }
        </div>            

        <div className='separator border-gray-200 mb-2'></div>

        <FormikNotesArea />
        
        <div className='separator border-gray-200 mb-2'></div>
        <StatusErrorMessage status={status} />
        <SubmitButtonRow doClose={doClose} />
      </div>
    </Form>
    </>
    )   
  }}
  </Formik>
  </>
  )
}

export { SubmitSignedROIForm }